import { CContainer, CRow, CCol, CFormInput, CButton, CFormSelect } from '@coreui/react';
import { useEffect, useContext, useState } from 'react';
import { CustomBreadcrumbs, LoadingOverlay } from '../../components';
import QrTagBatchTable from '../../components/Table/QrTagBatchTable';
import { AppContext } from '../../context/AppContext';
import qrIcon from '../../assets/images/cil-qr-code.svg'; // Ajustar ruta según tu proyecto
import styles from './ListQrTagBatches.module.css';
import { useNavigate } from 'react-router-dom';

const ListQrTagBatches = () => {
  const { getQrTagBatches, downloadQrSheet } = useContext(AppContext);
  const navigate = useNavigate();
  // Estado local de la página (NO en Context)
  const [batches, setBatches] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    per_page: 10,
    page: 1,
  });

  // Columnas para la tabla
  const columns = [
    { key: 'id', label: '#', _props: { scope: 'col' } },
    { key: 'company_name', label: 'Empresa', _props: { scope: 'col' } },
    { key: 'qr_tag_batch_status', label: 'Estado', _props: { scope: 'col' } },
    { key: 'owner_name', label: 'Propietario', _props: { scope: 'col' } },
    { key: 'email', label: 'Email', _props: { scope: 'col' } },
    { key: 'badge_size_name', label: 'Tamaño Badge', _props: { scope: 'col' } },
    { key: 'qr_tags_amount', label: 'Cantidad QRs', _props: { scope: 'col' } },
    { key: 'created_at', label: 'Creado', _props: { scope: 'col' } },
    { key: 'actions', label: 'Acciones', _props: { scope: 'col' } },
  ];

  // ✅ NUEVO: Handler para descargar hoja de QRs
  const handleDownloadQrSheet = async (batchId, batchCompanyName) => {
    setDownloading(true);
    
    try {
      // Usar fetch en lugar de axios para manejar blobs correctamente
      const response = await downloadQrSheet(batchId);

      // Obtener el blob (archivo binario)
      const blob = await response.data;
      
      // Crear URL temporal para el blob
      const url = window.URL.createObjectURL(blob);
      
      // Crear link temporal para descargar
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-batch-${batchId}-${Date.now()}.jpg`; // Nombre del archivo
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading QR sheet:', error);
      alert('Error al descargar la hoja de QRs. Por favor, intentá de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  // Fetch de batches con filtros y paginación
  const fetchBatches = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = { ...filters, ...params };
      const response = await getQrTagBatches(queryParams);
      
      // Asumimos que el backend devuelve: { data: [...], links: {...}, meta: {...} }
      setBatches(response.data || []);
      setPagination(response.meta || response.links || null);
    } catch (error) {
      console.error('Error fetching batches:', error);
      // Aquí podrías mostrar un toast de error
    } finally {
      setLoading(false);
    }
  };

  // Handlers para filtros
  const handleFilterChange = (e) => {
    const { id, value } = e.target;
    setFilters(prev => ({ ...prev, [id]: value, page: 1 })); // Resetear a página 1 al filtrar
  };

  const handleSearch = () => {
    fetchBatches({ page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({ name: '', per_page: 10, page: 1 });
    fetchBatches({ name: '', page: 1 });
  };

  // Handler para paginación (cuando se hace clic en página siguiente/anterior)
  const handlePageChange = (url) => {
    if (!url) return;
    // Extraer query params de la URL de paginación del backend
    const urlObj = new URL(url);
    const params = Object.fromEntries(urlObj.searchParams);
    fetchBatches(params);
  };

  // Efecto inicial: cargar batches al montar el componente
  useEffect(() => {
    fetchBatches();
  }, []);

  // Transformar datos para la tabla (aplanar relaciones anidadas)
  const tableData = batches.map(batch => ({
    id: batch.id,
    company_name: batch.company?.name || batch.company_name || '—',
    qr_tag_batch_status: batch?.qr_tag_batch_status?.name || 'Pendiente',
    owner_name: batch.owner_name || '—',
    email: batch.email || '—',
    badge_size_name: batch.badge_size?.name || '—',
    qr_tags_amount: batch.qr_tags_amount || 0,
    created_at: batch.created_at ? new Date(batch.created_at).toLocaleDateString('es-AR') : '—',
    // Acciones: objeto con datos para los botones
    actions: {
      view: `/dashboard/qr-tag-batches/view/${batch.id}`,
      edit: `/dashboard/qr-tag-batches/edit/${batch.id}`,
      download: `/dashboard/qr-tag-batches/${batch.id}/download-qr-sheet`,
      hasQrTags: batch.qr_tags_amount > 0,
    },
  }));

  return (
    <div>
      <LoadingOverlay 
        isVisible={downloading}
        message="📥 Descargando hoja de QRs, espere unos instantes"
      />
      <CustomBreadcrumbs parentTitle={'QR Tags'} activeTitle="Lotes" />
      
      <CContainer className="mt-4">
        {/* Resumen / Métricas */}
        <CRow className="mb-4">
          <CCol xs={12}>
            <h2 className="title">Resumen</h2>
          </CCol>
          <CCol xs={12} lg={4}>
            {/* Card simple con total de batches */}
            <div className="card border-primary mb-3">
              <div className="card-body d-flex align-items-center">
                <img src={qrIcon} alt="QR" style={{ width: 40, height: 40, marginRight: 12 }} />
                <div>
                  <h3 className="mb-0">{pagination?.total || 0}</h3>
                  <small className="text-muted">Lotes creados</small>
                </div>
              </div>
            </div>
          </CCol>
          {/* Podés agregar más cards si necesitás más métricas */}
        </CRow>

        {/* Filtros */}
        <CRow className="mb-4">
          <CCol xs={12} lg={6}>
            <h2 className="title mb-3">Lotes de QR Tags</h2>
          </CCol>
          <CCol xs={12} lg={6} className="d-flex justify-content-end">
            <CButton 
              size="lg" 
              color="primary" 
              onClick={() => navigate('/dashboard/qr-tag-batches/create')}
              className={styles.button}
            >
              + Crear Lote
            </CButton>
          </CCol>
          <CCol xs={12} lg={12} className="pt-2 mt-3">
            <div className={styles.formContainer}>
              <CFormInput
                type="search"
                id="name"
                placeholder="Buscar por nombre de empresa o propietario"
                value={filters.name}
                onChange={handleFilterChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <CFormSelect
                id="per_page"
                value={filters.per_page}
                onChange={handleFilterChange}
              >
                <option value="10">10 por página</option>
                <option value="25">25 por página</option>
                <option value="50">50 por página</option>
                <option value="100">100 por página</option>
              </CFormSelect>
              <div className="d-flex gap-2">
                <CButton color="primary" onClick={handleSearch} disabled={loading}>
                  {loading ? 'Buscando...' : 'Buscar'}
                </CButton>
                <CButton color="secondary" onClick={handleClearFilters}>
                  Limpiar
                </CButton>
              </div>
            </div>
          </CCol>
        </CRow>

        {/* Tabla de batches */}
        <CRow>
          <CCol xs={12}>
            {loading && batches.length === 0 ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-2 text-muted">Cargando lotes...</p>
              </div>
            ) : (
              <QrTagBatchTable
                columns={columns}
                data={tableData}
                pagination={pagination}
                onPageChange={handlePageChange}
                onDownload={handleDownloadQrSheet}
              />
            )}
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default ListQrTagBatches;